import * as THREE from 'three';
import { ball, box, leaf, tube } from './art';
import { NEST, PROVISIONS, type HomeProgress } from './homeData';

export class CarryHome {
  private readonly foods = new Map<string, THREE.Group>();
  private readonly upgrades: THREE.Group[] = [];
  private readonly carried: THREE.Group;
  constructor(scene: THREE.Scene, ant: THREE.Group, private readonly heightAt: (x: number, z: number) => number) {
    const nest = new THREE.Group(); nest.position.set(NEST.x, 0, NEST.z); scene.add(nest);
    ball(nest, 0, .05, -.7, 2, .15, 2, '#66513d');
    ball(nest, 0, .7, -2.1, 1.7, 1.65, 1.55, '#b39b73');
    // A dark inset and a twig arch make the mound read as a little house.
    ball(nest, 0, .63, -.67, .56, .76, .11, '#30382c');
    const arch = Array.from({ length: 17 }, (_, i) => {
      const a = Math.PI * i / 16;
      return new THREE.Vector3(Math.cos(a) * .64, .25 + Math.sin(a) * 1.13, -.52);
    });
    tube(nest, arch, .12, '#71634b');
    box(nest, 0, .12, -.2, 1.3, .18, .75, '#abac91');
    leaf(nest, -.85, 2.05, -.7, 3.3, 1.65, '#609354', -.18);
    leaf(nest, .85, 2.13, -.7, 3.3, 1.65, '#81a663', .18);
    ball(nest, 1.1, 1.03, -.99, .31, .32, .08, '#dce9b0');
    box(nest, 1.1, 1.03, -.88, .07, .6, .06, '#71634b');
    box(nest, 1.1, 1.03, -.88, .6, .07, .06, '#71634b');
    for (let i = 0; i < 9; i++) {
      const a = i / 9 * Math.PI * 2;
      ball(nest, Math.cos(a) * 1.65, .13, -1.8 + Math.sin(a) * 1.4, .25, .18, .24, '#919a8e');
    }
    for (let i = 0; i < 3; i++) {
      const upgrade = new THREE.Group(); nest.add(upgrade); this.upgrades.push(upgrade);
      if (i === 0) { const food = this.food(); food.position.set(1.6, 0, .4); upgrade.add(food); }
      if (i === 1) leaf(upgrade, -.8, .55, -.5, 2, 1.2, '#6eaa62', -.5);
      if (i === 2) for (let j = 0; j < 5; j++) ball(upgrade, -1.6 + Math.cos(j * 1.26) * .3, .35, .5 + Math.sin(j * 1.26) * .3, .22, .13, .22, '#eaa8b2');
    }
    for (const p of PROVISIONS) {
      const food = this.food(); food.position.set(p.x, 0, p.z); scene.add(food); this.foods.set(p.id, food);
      const ring = new THREE.Mesh(new THREE.RingGeometry(.8, .91, 32), new THREE.MeshBasicMaterial({ color: '#faf2b7', side: THREE.DoubleSide }));
      ring.rotation.x = -Math.PI / 2; ring.position.y = .07; food.add(ring);
    }
    this.carried = this.food(); this.carried.scale.setScalar(.65); this.carried.position.set(0, .6, -.65); ant.add(this.carried);
  }
  private food(): THREE.Group {
    const group = new THREE.Group();
    ball(group, 0, .33, 0, .58, .33, .42, '#d69a47');
    ball(group, -.18, .52, -.12, .24, .15, .2, '#f4d08b');
    ball(group, .25, .43, .12, .23, .17, .2, '#e9b968');
    return group;
  }
  setEyeView(active: boolean): void {
    this.carried.scale.setScalar(active ? .32 : .65);
    this.carried.position.set(active ? .15 : 0, active ? .02 : .6, active ? -.9 : -.65);
  }
  sync(state: HomeProgress): void {
    for (const p of PROVISIONS) {
      const model = this.foods.get(p.id)!; const point = state.dropped[p.id] ?? p;
      model.position.set(point.x, this.heightAt(point.x, point.z), point.z);
      model.visible = state.carrying !== p.id && !state.delivered.includes(p.id);
    }
    this.carried.visible = state.carrying !== null;
    this.upgrades.forEach((model, i) => { model.visible = state.delivered.length > i; });
  }
  action(state: HomeProgress, x: number, z: number): { id: string; label: string } | undefined {
    if (state.carrying) return Math.hypot(x - NEST.x, z - NEST.z) < 2 ? { id: 'deliver', label: 'Deliver crumb home' } : undefined;
    const food = PROVISIONS.find(p => {
      const point = state.dropped[p.id] ?? p;
      return !state.delivered.includes(p.id) && Math.hypot(x - point.x, z - point.z) < 1.8;
    });
    return food ? { id: food.id, label: 'Pick up crumb for home' } : undefined;
  }
}
